import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import './TradeExecutionModal.css';

const TradeExecutionModal = ({ isOpen, onClose, pool, onTradeExecuted }) => {
  const { showToast } = useToast();
  const [tradeData, setTradeData] = useState({
    tradeType: 'buy',
    tokenAddress: '',
    tokenSymbol: '',
    amount: '',
    slippageBps: 100,
    strategyReason: ''
  });

  const [tokenSearch, setTokenSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [tradePreview, setTradePreview] = useState(null);
  const [poolBalance, setPoolBalance] = useState(0);
  const [poolPositions, setPoolPositions] = useState([]);

  useEffect(() => {
    if (isOpen && pool) {
      fetchPoolBalance();
      fetchPoolPositions();
    }
  }, [isOpen, pool]);

  const fetchPoolBalance = async () => {
    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/balance`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPoolBalance(data.balance);
      }
    } catch (error) {
      console.error('Error fetching pool balance:', error);
    }
  };

  const fetchPoolPositions = async () => {
    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/positions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPoolPositions(data.positions || []);
      }
    } catch (error) {
      console.error('Error fetching pool positions:', error);
    }
  };

  const searchTokens = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/api/tokens/search?q=${encodeURIComponent(query)}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.tokens || []);
      }
    } catch (error) {
      console.error('Error searching tokens:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTokenSearch = (value) => {
    setTokenSearch(value);
    searchTokens(value);
  };

  const selectToken = (token) => {
    setTradeData(prev => ({
      ...prev,
      tokenAddress: token.address,
      tokenSymbol: token.symbol
    }));
    setTokenSearch(token.symbol);
    setSearchResults([]);
  };

  const selectFromPositions = (position) => {
    setTradeData(prev => ({
      ...prev,
      tokenAddress: position.tokenAddress,
      tokenSymbol: position.tokenSymbol,
      tradeType: 'sell'
    }));
    setTokenSearch(position.tokenSymbol);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTradeData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getTradePreview = async () => {
    if (!tradeData.tokenAddress || !tradeData.amount) {
      setTradePreview(null);
      return;
    }

    try {
      const response = await fetch('/api/admin/pools/trade-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          poolId: pool.id,
          tradeType: tradeData.tradeType,
          tokenAddress: tradeData.tokenAddress,
          amount: parseFloat(tradeData.amount),
          slippageBps: tradeData.slippageBps
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTradePreview(data.preview);
      }
    } catch (error) {
      console.error('Error getting trade preview:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      getTradePreview();
    }, 500);

    return () => clearTimeout(timer);
  }, [tradeData.tokenAddress, tradeData.amount, tradeData.tradeType, tradeData.slippageBps]);

  const executeTrade = async () => {
    if (!tradeData.tokenAddress || !tradeData.amount || !tradeData.strategyReason.trim()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    setIsExecuting(true);
    try {
      const response = await fetch('/api/admin/pools/execute-trade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          poolId: pool.id,
          tradeType: tradeData.tradeType,
          tokenAddress: tradeData.tokenAddress,
          amount: parseFloat(tradeData.amount),
          slippageBps: tradeData.slippageBps,
          strategyReason: tradeData.strategyReason
        })
      });

      if (response.ok) {
        const data = await response.json();
        showToast(`Trade executed successfully! TX: ${data.transactionHash}`, 'success');
        onTradeExecuted();
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to execute trade');
      }
    } catch (error) {
      console.error('Error executing trade:', error);
      showToast(error.message, 'error');
    } finally {
      setIsExecuting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="trade-execution-modal">
        <div className="modal-header">
          <h2>Execute Trade - {pool.name}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Pool Balance Info */}
          <div className="pool-info">
            <div className="balance-info">
              <span className="label">Pool Balance:</span>
              <span className="value">{poolBalance.toFixed(6)} SOL</span>
            </div>
          </div>

          {/* Trade Type Selection */}
          <div className="trade-type-section">
            <label>Trade Type</label>
            <div className="trade-type-buttons">
              <button
                className={`trade-type-btn ${tradeData.tradeType === 'buy' ? 'active' : ''}`}
                onClick={() => setTradeData(prev => ({ ...prev, tradeType: 'buy' }))}
              >
                Buy
              </button>
              <button
                className={`trade-type-btn ${tradeData.tradeType === 'sell' ? 'active' : ''}`}
                onClick={() => setTradeData(prev => ({ ...prev, tradeType: 'sell' }))}
              >
                Sell
              </button>
            </div>
          </div>

          {/* Current Positions (for sell trades) */}
          {tradeData.tradeType === 'sell' && poolPositions.length > 0 && (
            <div className="positions-section">
              <label>Current Positions</label>
              <div className="positions-list">
                {poolPositions.map((position, index) => (
                  <div 
                    key={index}
                    className="position-item"
                    onClick={() => selectFromPositions(position)}
                  >
                    <span className="token-symbol">{position.tokenSymbol}</span>
                    <span className="token-balance">{position.balance.toFixed(6)}</span>
                    <span className="token-value">{position.usdValue.toFixed(2)} USD</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Token Selection */}
          <div className="token-selection">
            <label htmlFor="tokenSearch">
              {tradeData.tradeType === 'buy' ? 'Token to Buy' : 'Token to Sell'}
            </label>
            <div className="token-search-container">
              <input
                type="text"
                id="tokenSearch"
                value={tokenSearch}
                onChange={(e) => handleTokenSearch(e.target.value)}
                placeholder="Search by symbol or address..."
                className="token-search-input"
              />
              
              {isSearching && (
                <div className="search-loading">Searching...</div>
              )}
              
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((token, index) => (
                    <div
                      key={index}
                      className="search-result-item"
                      onClick={() => selectToken(token)}
                    >
                      <div className="token-info">
                        <span className="token-symbol">{token.symbol}</span>
                        <span className="token-name">{token.name}</span>
                      </div>
                      <span className="token-price">${token.price?.toFixed(6) || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trade Amount */}
          <div className="form-group">
            <label htmlFor="amount">
              Amount ({tradeData.tradeType === 'buy' ? 'SOL' : tradeData.tokenSymbol || 'Tokens'})
            </label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={tradeData.amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              step="0.000001"
              min="0"
            />
          </div>

          {/* Slippage */}
          <div className="form-group">
            <label htmlFor="slippageBps">Slippage Tolerance (bps)</label>
            <select
              id="slippageBps"
              name="slippageBps"
              value={tradeData.slippageBps}
              onChange={handleInputChange}
            >
              <option value={50}>0.5%</option>
              <option value={100}>1%</option>
              <option value={300}>3%</option>
              <option value={500}>5%</option>
              <option value={1000}>10%</option>
            </select>
          </div>

          {/* Strategy Reason */}
          <div className="form-group">
            <label htmlFor="strategyReason">Strategy Reason *</label>
            <textarea
              id="strategyReason"
              name="strategyReason"
              value={tradeData.strategyReason}
              onChange={handleInputChange}
              placeholder="Explain the reasoning behind this trade for transparency and reporting"
              rows={3}
              required
            />
          </div>

          {/* Trade Preview */}
          {tradePreview && (
            <div className="trade-preview">
              <h4>Trade Preview</h4>
              <div className="preview-details">
                <div className="preview-row">
                  <span>Estimated Output:</span>
                  <span>{tradePreview.estimatedOutput?.toFixed(6)} {tradePreview.outputToken}</span>
                </div>
                <div className="preview-row">
                  <span>Price Impact:</span>
                  <span className={tradePreview.priceImpact > 5 ? 'high-impact' : ''}>
                    {tradePreview.priceImpact?.toFixed(2)}%
                  </span>
                </div>
                <div className="preview-row">
                  <span>Estimated Fees:</span>
                  <span>{tradePreview.fees?.toFixed(6)} SOL</span>
                </div>
                <div className="preview-row">
                  <span>Minimum Received:</span>
                  <span>{tradePreview.minimumReceived?.toFixed(6)} {tradePreview.outputToken}</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="modal-actions">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              className="execute-btn"
              onClick={executeTrade}
              disabled={isExecuting || !tradeData.tokenAddress || !tradeData.amount || !tradeData.strategyReason.trim()}
            >
              {isExecuting ? 'Executing...' : `Execute ${tradeData.tradeType.toUpperCase()}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeExecutionModal;